import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { 
  AlertCircle, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Mail, 
  Twitter, 
  Globe, 
  CheckCircle2, 
  Loader2,
  Info
} from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useOnboardingConfig } from "@/components/onboarding/OnboardingSteps"

export default function NPOMicrosite({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [newDomain, setNewDomain] = useState("")
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const { updateConfig } = useOnboardingConfig();

  // Fetch existing domain
  const { data: domainData } = useQuery({
    queryKey: ["npo-domain", profile?.id],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/domain`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      
      if (!response.ok) {
        throw new Error("Failed to fetch domain settings")
      }

      return response.json()
    },
    enabled: !!session?.access_token,
  })

  // Add domain mutation
  const addDomainMutation = useMutation({
    mutationFn: async (domainSuffix: string) => {
      // Validate domain format
      if (!/^[a-z0-9-]+$/.test(domainSuffix)) {
        throw new Error("Domain can only contain lowercase letters, numbers, and hyphens")
      }

      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/domain`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            domain_suffix: domainSuffix.toLowerCase(),
          }),
        }
      )

      if (!response.ok) {
        throw new Error("Failed to create domain")
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["npo-domain", profile?.id] })
      toast({
        title: "Domain added successfully",
        description: "Your microsite subdomain has been configured.",
      })
      setNewDomain("")
      setShowConfirmDialog(false)
      updateConfig({ missionhub: true })
    },
    onError: (error: any) => {
      toast({
        title: "Error adding domain",
        description: error.message,
        variant: "destructive",
      })
      setShowConfirmDialog(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newDomain) {
      toast({
        title: "Error",
        description: "Please enter a domain suffix",
        variant: "destructive",
      })
      return
    }
    setShowConfirmDialog(true)
  }

  const confirmAddDomain = () => {
    addDomainMutation.mutate(newDomain)
  }

  const handleShare = (platform: string) => {
    const url = `https://${domainData?.data?.domain_suffix}.causeconnect.ai`
    const text = "Check out our organization's page!"

    switch (platform) {
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank')
        break
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
        break
      case 'instagram':
        // Instagram doesn't have a direct sharing URL, we'll copy to clipboard instead
        navigator.clipboard.writeText(url)
        toast({
          title: "URL copied to clipboard",
          description: "You can now paste this URL on Instagram",
        })
        break
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent("Check out our organization's page")}&body=${encodeURIComponent(`${text}\n${url}`)}`
        break
    }
  }

  return (
    <div className="space-y-6">


      {domainData?.data ? (
        <div className="grid gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Your Microsite URL</h3>
              <Badge variant={domainData?.data?.is_active ? "default" : "secondary"}>
                {domainData?.data?.is_active ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Ready
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Configuring
                  </span>
                )}
              </Badge>
            </div>
            <p className="text-blue-600">
              <a 
                href={`https://${domainData?.data?.domain_suffix}.causeconnect.ai`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-1"
              >
                <Globe className="h-4 w-4" />
                https://{domainData?.data?.domain_suffix}.causeconnect.ai
              </a>
            </p>
            {!domainData?.data?.is_active && (
              <Alert variant="info" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please wait while we configure your domain. You will be notified via email when your microsite is ready.
                </AlertDescription>
              </Alert>
            )}
            <div className="mt-6">
              <h4 className="text-sm font-medium mb-3">Share your microsite</h4>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('linkedin')}
                  title="Share on LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('twitter')}
                  title="Share on X (Twitter)"
                >
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('facebook')}
                  title="Share on Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('instagram')}
                  title="Copy URL for Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('email')}
                  title="Share via Email"
                >
                  <Mail className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>


        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="domain" className="block text-sm font-medium mb-2">
              Choose your subdomain
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                className="max-w-[200px]"
                placeholder="your-org"
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens are allowed"
              />
              <span>.causeconnect.ai</span>
            </div>
          </div>
          <Button type="submit">Add Domain</Button>
        </form>
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Domain Addition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to add the subdomain "{newDomain}.causeconnect.ai"? 
              This will be your organization's permanent microsite URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAddDomain}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
